import React, {useContext, useEffect, useMemo} from 'react';
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { PAContext } from "../../../Services/PAContext";
import { Box } from "@mui/material";
import {Stack} from "@mui/system";

function ConciergeMessageCard(props) {

    const { dataArray, markMessageAsExecuted } = useContext(PAContext);
    const { Attendees } = dataArray;
    const navigate = useNavigate();
    const message = props.message;

    // --- INTERNAL STYLES FOR FRITZ'S TABLES ---
    const tableStyles = {
        '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '12px',
            marginBottom: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '8px',
            overflow: 'hidden',
            fontSize: '13px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            '& th': {
                textAlign: 'left',
                borderBottom: '2px solid rgba(25, 118, 210, 0.2)',
                padding: '10px 8px',
                color: '#1976d2',
                fontWeight: '600',
                backgroundColor: 'rgba(25, 118, 210, 0.05)'
            },
            '& td': {
                padding: '8px',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                color: '#2c3e50'
            },
            '& tr:last-child td': {
                borderBottom: 'none'
            },
            '& tr:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
            }
        }
    };

    // --- 1. THE SWITCHBOARD ---
    const handleAction = (target) => {
        if (!target) return;

        // Handle Special Dialogs
        if (target === "DIALOG_ACCOUNT") {
            props.setShowAccountDialog(true);
            return;
        }
        if (target === "DIALOG_REWARD_POINTS") {
            props.setEventPointsDialogOpen(true);
            return;
        }

        // Handle Feature Switching + Routing
        const pathParts = target.split('/');
        const featureName = pathParts[pathParts.length - 1];

        if (props.setCurrentFeature && featureName) {
            props.setCurrentFeature(featureName);
        }

        // Clean trailing slashes for comparison
        const cleanCurrent = window.location.pathname.replace(/\/$/, "");
        const cleanTarget = target.replace(/\/$/, "");

        if (cleanCurrent !== cleanTarget) {
            navigate(target);
        }
    };

// --- 2. PARSE MESSAGE (Memoized for performance) ---
    const { cleanText, suggestions, immediateNav } = useMemo(() => {
        // 🎯 FORCE STRING: Ensure we are working with a string, even if an object slips in
        let rawData = message.message;
        let text = "";

        if (typeof rawData === 'string') {
            text = rawData;
        } else {
            // This stops the .replace() crash
            text = "";
            if (rawData !== undefined && rawData !== null) {
                console.warn("Received non-string message content:", rawData);
            }
        }
        const foundSuggestions = [];
        let navTarget = null;

        // Now .replace() is safe because 'text' is guaranteed to be a string
        text = text.replace(/```html/g, "").replace(/```/g, "").trim();

        // ... (rest of your regex logic)
        const offerRegex = /\[\[OFFER_NAV:\s*(.*?)\s*\|\s*(.*?)\s*\]\]/gi;
        let match;
        while ((match = offerRegex.exec(text)) !== null) {
            foundSuggestions.push({
                label: match[1].trim(),
                route: match[2].trim()
            });
        }
        text = text.replace(offerRegex, "").trim();

        const navMatch = text.match(/\[\[NAVIGATE:\s*(.*?)\s*\]\]/i);
        if (navMatch) {
            navTarget = navMatch[1].trim();
            text = text.replace(navMatch[0], "").trim();
        }

        return { cleanText: text, suggestions: foundSuggestions, immediateNav: navTarget };
    }, [message.message]);

    // --- 3. AUTO-EXECUTE NAVIGATE ---
    useEffect(() => {
        if (immediateNav && message.isFinal && !message.wasExecuted) {
            console.log("🚀 Concierge Auto-Navigating to:", immediateNav);
            handleAction(immediateNav);
            markMessageAsExecuted(message.id, message.created);
        }
    }, [immediateNav, message.isFinal, message.wasExecuted]);

    const getSenderName = () => {
        if (message.attendee_id === "GEMINI_AI") return "Fritz (Concierge)";
        const att = Attendees?.find((_att) => _att.id === message.attendee_id);
        return att ? att.nickname : "User";
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: props.align === 'left' ? 'flex-start' : 'flex-end',
            width: '100%',
            padding: '4px 0'
        }}>
            <Box sx={{
                position: 'relative',
                maxWidth: '85%',
                backgroundColor: props.align === 'left' ? '#f0f4f8' : '#d1e3ff',
                padding: '12px 16px',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: message.priority ? "2px solid #ff1744" : "none",
                transition: 'all 0.2s ease',
                ...tableStyles
            }}>
                {/* Message Content */}
                <Box sx={{
                    fontSize: '14px',
                    lineHeight: '1.5',
                    color: '#2c3e50',
                    '& .fritz-content': {
                        overflowX: 'auto',
                        wordBreak: 'break-word'
                    }
                }}>
                    <div
                        className="fritz-content"
                        dangerouslySetInnerHTML={{ __html: cleanText }}
                    />
                </Box>

                {/* Navigation Suggestions (Buttons) */}
                {suggestions.length > 0 && (
                    <Stack spacing={1} sx={{
                        mt: 2,
                        pt: 1.5,
                        borderTop: '1px solid rgba(0,0,0,0.08)'
                    }}>
                        {suggestions.map((s, idx) => (
                            <Button
                                key={idx}
                                variant="contained"
                                size="small"
                                fullWidth
                                startIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                                onClick={() => handleAction(s.route)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    py: 0.8,
                                    backgroundColor: '#1976d2',
                                    '&:hover': { backgroundColor: '#115293' },
                                    boxShadow: 'none'
                                }}
                            >
                                Open {s.label}
                            </Button>
                        ))}
                    </Stack>
                )}

                {/* Footer: Name and Time */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '10px'
                }}>
                    {props.align === 'left' && (
                        <Typography sx={{
                            fontSize: '10px',
                            fontWeight: '800',
                            color: '#546e7a',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            {getSenderName()}
                        </Typography>
                    )}
                    <Typography sx={{
                        fontSize: '9px',
                        color: '#90a4ae',
                        marginLeft: 'auto'
                    }}>
                        {dayjs(message.created).format('h:mm A DD MMM, YYYY')}
                    </Typography>
                </div>
            </Box>
        </div>
    );
}

export default ConciergeMessageCard;